// The cash drawer and the order history.
//
// [19:15] "Then we have sales, and then we have the point of sales system. So
// it keeps track. It has a register for people who paid cash. It has payment
// methods. We can go back in and see where the payments are."
//
// Two tabs: the drawer (open it, pay in, pay out, count it, close it) and
// every order, where a receipt can be printed, emailed or refunded.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Banknote, Receipt, Plus, Minus, Calculator, Search, Printer, RotateCcw, Lock, Unlock, Ban,
} from "lucide-react";
import {
  Skeleton, useToast, useConfirm, EmptyState, Button, Modal, ModalFooter, Input, Textarea, Select, Badge,
} from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { useCan } from "@/lib/me";
import { formatMoney, formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

interface Totals {
  opening_float_cents: number; cash_sales_cents: number; cash_refunds_cents: number;
  pay_in_cents: number; pay_out_cents: number; expenses_cents: number;
  deposits_cents: number; expected_cents: number;
}
interface Session {
  id: string; opened_at: string; opened_by_name: string | null;
  opening_float_cents: number; closed_at: string | null; closed_by_name: string | null;
  counted_cents: number | null; expected_cents: number | null; variance_cents: number | null;
  note: string | null;
}
interface Movement {
  id: string; kind: string; amount_cents: number; reason: string;
  created_by_name: string | null; created_at: string;
}
interface Order {
  id: string; number: string | null; status: string; total_cents: number;
  paid_at: string | null; created_at: string; contact_id: string | null;
  client_name: string; refunded_cents: number; payment_method: string | null;
  /** Rung up in practice mode (deleted when practice ends). */
  practice?: boolean;
  /** "online_shop" for web-shop orders. */
  source?: string | null;
  /** Web-shop order handed over or posted. */
  fulfilled_at?: string | null;
}

const METHOD_LABEL: Record<string, string> = {
  cash: "Cash", card_external: "Her own card machine", card_link: "Card (link)",
  card_on_file: "Card on file", none: "Unpaid",
};

export function RegisterPage() {
  const [tab, setTab] = useState<"drawer" | "orders">("drawer");
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Sales</h1>
        <div className="flex gap-1 rounded-lg border border-zinc-800 bg-zinc-900/50 p-0.5">
          {([["drawer", "Cash drawer"], ["orders", "Orders & receipts"]] as const).map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={cn("rounded-md px-3 py-1.5 text-sm",
                tab === k ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200")}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {tab === "drawer" ? <Drawer /> : <Orders />}
    </div>
  );
}

// ─── Cash drawer ───────────────────────────────────────────────────────────

function Drawer() {
  const toast = useToast();
  const [session, setSession] = useState<Session | null>(null);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [history, setHistory] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState<"pay_in" | "pay_out" | "expense" | "deposit" | null>(null);
  const [counting, setCounting] = useState(false);
  const [opening, setOpening] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get<{ session: Session | null; totals: Totals | null; movements: Movement[]; history: Session[] }>("/pos/register")
      .then((d) => { setSession(d.session); setTotals(d.totals); setMovements(d.movements); setHistory(d.history); })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Couldn't load the register"))
      .finally(() => setLoading(false));
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Skeleton className="h-96" />;

  if (!session) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-8 text-center">
          <Lock className="mx-auto h-8 w-8 text-zinc-600" />
          <h2 className="mt-3 text-lg font-semibold text-zinc-100">The drawer is closed</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-zinc-500">
            Open it at the start of the day with however much float you're putting in.
            Cash taken at checkout is counted against it.
          </p>
          <Button className="mt-4" onClick={() => setOpening(true)}>
            <Unlock className="h-4 w-4" /> Open the drawer
          </Button>
        </div>
        <ClosedHistory history={history} />
        {opening && <OpenDrawerModal onClose={() => setOpening(false)} onDone={() => { setOpening(false); load(); }} />}
      </div>
    );
  }

  const rows: { label: string; value: number; muted?: boolean }[] = [
    { label: "Opening float", value: totals?.opening_float_cents ?? 0, muted: true },
    { label: "Cash taken", value: totals?.cash_sales_cents ?? 0 },
    { label: "Cash refunded", value: -(totals?.cash_refunds_cents ?? 0) },
    { label: "Paid in", value: totals?.pay_in_cents ?? 0 },
    { label: "Paid out", value: -(totals?.pay_out_cents ?? 0) },
    { label: "Expenses", value: -(totals?.expenses_cents ?? 0) },
    { label: "Taken to the bank", value: -(totals?.deposits_cents ?? 0) },
  ];

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="lg:col-span-2 space-y-3">
        <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30">
          <div className="flex items-center justify-between border-b border-zinc-800/60 px-4 py-2.5">
            <div>
              <h2 className="text-sm font-semibold text-zinc-100">Drawer open</h2>
              <p className="text-[11px] text-zinc-500">
                Since {formatDate(session.opened_at, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                {session.opened_by_name ? ` · ${session.opened_by_name}` : ""}
              </p>
            </div>
            <Badge color="emerald" size="xs">Open</Badge>
          </div>
          <ul className="divide-y divide-zinc-800/40">
            {rows.map((r) => (
              <li key={r.label} className="flex items-center justify-between px-4 py-2 text-sm">
                <span className={r.muted ? "text-zinc-500" : "text-zinc-300"}>{r.label}</span>
                <span className={cn("tabular-nums", r.value < 0 ? "text-rose-300" : "text-zinc-200")}>
                  {/* Zero is zero — never "−$0.00". */}
                  {r.value < 0 ? `(${formatMoney(Math.abs(r.value))})` : formatMoney(Math.abs(r.value))}
                </span>
              </li>
            ))}
            <li className="flex items-center justify-between bg-zinc-900/60 px-4 py-2.5">
              <span className="text-sm font-semibold text-zinc-100">Should be in the drawer</span>
              <span className="text-base font-semibold tabular-nums text-zinc-100">
                {formatMoney(totals?.expected_cents ?? 0)}
              </span>
            </li>
          </ul>
          <div className="flex flex-wrap gap-2 border-t border-zinc-800/60 p-3">
            <Button size="sm" variant="secondary" onClick={() => setMoving("pay_in")}>
              <Plus className="h-3.5 w-3.5" /> Pay in
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setMoving("pay_out")}>
              <Minus className="h-3.5 w-3.5" /> Pay out
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setMoving("expense")}>Expense</Button>
            <Button size="sm" variant="secondary" onClick={() => setMoving("deposit")}>To the bank</Button>
            <Button size="sm" className="ml-auto" onClick={() => setCounting(true)}>
              <Calculator className="h-3.5 w-3.5" /> Count &amp; close
            </Button>
          </div>
        </div>

        {movements.length > 0 && (
          <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30">
            <h3 className="border-b border-zinc-800/60 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
              Money in and out
            </h3>
            <ul className="divide-y divide-zinc-800/40">
              {movements.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                  <div className="min-w-0">
                    <p className="text-zinc-300">
                      {{ pay_in: "Paid in", pay_out: "Paid out", expense: "Expense", deposit: "To the bank" }[m.kind] ?? m.kind}
                      {m.reason ? <span className="text-zinc-500"> — {m.reason}</span> : null}
                    </p>
                    <p className="text-[11px] text-zinc-600">
                      {formatDate(m.created_at, { hour: "numeric", minute: "2-digit" })}
                      {m.created_by_name ? ` · ${m.created_by_name}` : ""}
                    </p>
                  </div>
                  <span className={cn("shrink-0 tabular-nums",
                    m.kind === "pay_in" ? "text-emerald-300" : "text-rose-300")}>
                    {m.kind === "pay_in" ? "" : "−"}{formatMoney(m.amount_cents)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <ClosedHistory history={history} />

      {moving && (
        <MovementModal kind={moving} onClose={() => setMoving(null)}
          onDone={() => { setMoving(null); load(); }} />
      )}
      {counting && totals && (
        <CountModal expected={totals.expected_cents} onClose={() => setCounting(false)}
          onDone={() => { setCounting(false); load(); }} />
      )}
    </div>
  );
}

function ClosedHistory({ history }: { history: Session[] }) {
  if (history.length === 0) return <div />;
  return (
    <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30">
      <h3 className="border-b border-zinc-800/60 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
        Previous drawers
      </h3>
      <ul className="divide-y divide-zinc-800/40">
        {history.map((h) => (
          <li key={h.id} className="px-4 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-zinc-300">
                {h.closed_at && formatDate(h.closed_at, { month: "short", day: "numeric" })}
              </span>
              {h.variance_cents === 0 ? (
                <Badge color="emerald" size="xs">Balanced</Badge>
              ) : (
                <Badge color={(h.variance_cents ?? 0) > 0 ? "sky" : "rose"} size="xs">
                  {(h.variance_cents ?? 0) > 0 ? "Over " : "Short "}
                  {formatMoney(Math.abs(h.variance_cents ?? 0))}
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-zinc-600">
              Counted {formatMoney(h.counted_cents ?? 0)} against {formatMoney(h.expected_cents ?? 0)}
              {h.closed_by_name ? ` · ${h.closed_by_name}` : ""}
            </p>
            {h.note && <p className="mt-0.5 text-[11px] text-zinc-500">{h.note}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function OpenDrawerModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [amount, setAmount] = useState("200.00");
  const [saving, setSaving] = useState(false);
  const go = async () => {
    setSaving(true);
    try {
      await api.post("/pos/register/open", { opening_float_cents: Math.round(parseFloat(amount || "0") * 100) });
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't open the drawer");
    } finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title="Open the drawer">
      <label className="mb-1 block text-xs text-zinc-400">How much cash are you starting with?</label>
      <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" autoFocus />
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={go} disabled={saving}>Open</Button>
      </ModalFooter>
    </Modal>
  );
}

const MOVEMENT_TITLE: Record<string, string> = {
  pay_in: "Put cash in", pay_out: "Take cash out",
  expense: "Pay an expense from the drawer", deposit: "Take money to the bank",
};

function MovementModal({ kind, onClose, onDone }: { kind: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const go = async () => {
    setSaving(true);
    try {
      await api.post("/pos/register/movement", {
        kind, amount_cents: Math.round(parseFloat(amount || "0") * 100), reason,
      });
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't record that");
    } finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title={MOVEMENT_TITLE[kind] ?? "Movement"}>
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs text-zinc-400">How much?</label>
          <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" autoFocus placeholder="0.00" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-zinc-400">What for?</label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Petty cash for supplies" />
        </div>
      </div>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={go} disabled={saving || !amount}>Record it</Button>
      </ModalFooter>
    </Modal>
  );
}

function CountModal({ expected, onClose, onDone }: { expected: number; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [counted, setCounted] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const countedCents = Math.round(parseFloat(counted || "0") * 100);
  const variance = counted === "" ? null : countedCents - expected;

  const go = async () => {
    setSaving(true);
    try {
      await api.post("/pos/register/close", { counted_cents: countedCents, note });
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't close the drawer");
    } finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title="Count the drawer">
      <div className="space-y-3">
        <p className="text-sm text-zinc-400">
          The system expects <span className="font-semibold text-zinc-200">{formatMoney(expected)}</span> in the drawer.
          Count what's actually there.
        </p>
        <div>
          <label className="mb-1 block text-xs text-zinc-400">How much did you count?</label>
          <Input value={counted} onChange={(e) => setCounted(e.target.value)} inputMode="decimal" autoFocus placeholder="0.00" />
        </div>
        {variance !== null && (
          <p className={cn("rounded-lg px-3 py-2 text-sm",
            variance === 0 ? "bg-emerald-500/10 text-emerald-300"
              : variance > 0 ? "bg-sky-500/10 text-sky-300" : "bg-rose-500/10 text-rose-300")}>
            {variance === 0 ? "That balances exactly."
              : `${variance > 0 ? "Over" : "Short"} by ${formatMoney(Math.abs(variance))}.`}
          </p>
        )}
        <div>
          <label className="mb-1 block text-xs text-zinc-400">Note (optional)</label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
        </div>
      </div>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={go} disabled={saving || counted === ""}>Close the drawer</Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Orders ────────────────────────────────────────────────────────────────

function Orders() {
  const toast = useToast();
  const [orders, setOrders] = useState<Order[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<Order | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get<{ orders: Order[] }>("/pos/orders", { search, limit: 100 })
      .then((d) => setOrders(d.orders))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Couldn't load orders"))
      .finally(() => setLoading(false));
  }, [search, toast]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-3">
      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Receipt number or client…" className="h-8 pl-8 text-sm" />
      </div>

      {loading ? <Skeleton className="h-96" />
        : orders.length === 0 ? (
          <EmptyState icon={Receipt} title="No orders yet" description="Rung-up sales show here." />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-zinc-800/70">
            <table className="w-full min-w-max text-sm">
              <thead className="bg-zinc-900/50 text-zinc-500">
                <tr className="text-left text-[11px] uppercase tracking-wider">
                  <th className="px-3 py-2 font-medium">Receipt</th>
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium">Paid by</th>
                  <th className="px-3 py-2 text-right font-medium">Total</th>
                  <th className="px-3 py-2 text-right font-medium">Refunded</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-zinc-800/40 hover:bg-zinc-900/40">
                    <td className="px-3 py-2 font-medium text-zinc-100">
                      {o.number ?? "—"}
                      {o.practice && <Badge color="amber" size="xs" className="ml-2">Practice</Badge>}
                      {o.status === "void" && <Badge color="zinc" size="xs" className="ml-2">Voided</Badge>}
                      {o.source === "online_shop" && (
                        <Badge color={o.fulfilled_at ? "zinc" : "emerald"} size="xs" className="ml-2">
                          {o.status !== "paid" ? "Online, unpaid" : o.fulfilled_at ? "Online, done" : "Online order"}
                        </Badge>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-zinc-400">
                      {formatDate(o.paid_at ?? o.created_at, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </td>
                    <td className="px-3 py-2">
                      {o.contact_id ? (
                        <Link to={`/contacts/${o.contact_id}`} className="text-zinc-200 hover:text-indigo-300">{o.client_name}</Link>
                      ) : o.client_name}
                    </td>
                    <td className="px-3 py-2 text-zinc-400">{METHOD_LABEL[o.payment_method ?? ""] ?? "—"}</td>
                    <td className={cn("px-3 py-2 text-right tabular-nums",
                      o.status === "void" ? "text-zinc-600 line-through" : "text-zinc-200")}>
                      {formatMoney(o.total_cents)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {Number(o.refunded_cents) > 0
                        ? <span className="text-rose-300">({formatMoney(Number(o.refunded_cents))})</span>
                        : <span className="text-zinc-700">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setOpen(o)}
                        className="rounded-md px-2 py-1 text-[11px] text-indigo-300 hover:bg-zinc-800">
                        Receipt
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {open && <ReceiptModal order={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}

interface Payment { provider: string; amount_cents: number; payment_method: Record<string, unknown>; charged_at: string | null }
interface Redemption { sessions: number; service_name: string | null; price_cents: number; package_name: string | null }
interface Refund { id: string; amount_cents: number; method: string; reason: string; created_at: string; created_by_name: string | null }

function ReceiptModal({ order, onClose, onChanged }: { order: Order; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  // Undoing a sale moves money out of the takings, same as a refund, so it is
  // the same permission and the button is not offered to anyone without it.
  const canRefund = useCan("refund");
  const [data, setData] = useState<{
    order: Record<string, unknown>; payments: Payment[]; redemptions: Redemption[]; refunds: Refund[];
  } | null>(null);
  const [refunding, setRefunding] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voided, setVoided] = useState(order.status === "void");

  const load = useCallback(() => {
    api.get<typeof data>(`/pos/orders/${order.id}`).then(setData).catch(() => setData(null));
  }, [order.id]);
  useEffect(() => { load(); }, [load]);

  const lines = (data?.order?.line_items as { kind?: string; name: string; qty: number; unit_amount_cents: number; meta?: Record<string, unknown> }[]) ?? [];
  /**
   * The ticket, by whose treatment each line is.
   *
   * A line names its owner when several people were rung up together; the
   * rest belong to whoever the ticket is in the name of. One person means
   * one group, so an ordinary sale reads exactly as it always did.
   */
  const groups = (() => {
    const out: { key: string; name: string; total: number; lines: typeof lines }[] = [];
    for (const l of lines) {
      const m = (l.meta ?? {}) as { for_contact_id?: string; for_name?: string };
      const key = m.for_contact_id ?? "payer";
      const name = m.for_contact_id ? (m.for_name ?? "") : (order.client_name ?? "");
      let g = out.find((x) => x.key === key);
      if (!g) { g = { key, name, total: 0, lines: [] }; out.push(g); }
      g.lines.push(l);
      g.total += l.unit_amount_cents * l.qty;
    }
    return out;
  })();
  const refundedSoFar = (data?.refunds ?? []).reduce((s, r) => s + Number(r.amount_cents), 0);
  const left = order.total_cents - refundedSoFar;
  const meta = (data?.order?.metadata ?? {}) as { source?: string; delivery?: string; notes?: string | null;
    ship_to?: { name?: string | null; line1?: string | null; line2?: string | null; city?: string | null; state?: string | null; postal_code?: string | null } };
  const online = meta.source === "online_shop";
  const [fulfilledAt, setFulfilledAt] = useState<string | null>(order.fulfilled_at ?? null);
  /**
   * "It says paid on the machine but it did not pay it."
   *
   * Only offered where it can be true: a ticket whose every payment was typed
   * in by hand (cash, or her own card machine), nothing refunded. A card
   * payment is real money and belongs to Refund, and the server refuses it
   * either way.
   */
  const handTyped = (data?.payments ?? []).every((p) => p.provider === "manual" || p.provider === "cash");
  const canVoid = canRefund && !!data && !voided && refundedSoFar === 0 && handTyped
    && (order.status === "paid" || order.status === "open")
    && !order.practice;

  const doVoid = async () => {
    const vouchers = lines.filter((l) => l.kind === "package").length;
    const ok = await confirm({
      title: `Take ${order.number ?? "this ticket"} off the books?`,
      description: (
        <>
          <p>
            {formatMoney(order.total_cents)} comes back off your takings, because it never
            arrived. Use this when a sale was marked paid by mistake - not for money you are
            giving back, which is a refund.
          </p>
          <p className="mt-2 text-zinc-400">
            Their visit stays exactly as it is.
            {vouchers > 0 && " Any doses this ticket put on their file come back off too."}
          </p>
        </>
      ),
      confirmLabel: "Yes, it never got paid",
      danger: true,
    });
    if (!ok) return;
    setVoiding(true);
    try {
      await api.post(`/pos/orders/${order.id}/void`, { reason: "Marked paid, no money taken" });
      setVoided(true);
      onChanged();
      toast.success("Taken back off the books", "The sale is voided. Their visit is untouched.");
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't void that ticket");
    } finally { setVoiding(false); }
  };

  const markFulfilled = async () => {
    try {
      await api.post(`/pos/orders/${order.id}/fulfilled`, {});
      setFulfilledAt(new Date().toISOString());
      onChanged();
      toast.success(meta.delivery === "ship" ? "Marked as shipped" : "Marked as picked up");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't mark it");
    }
  };

  return (
    <Modal open onClose={onClose} title={`Receipt ${order.number ?? ""}`} size="lg">
      {!data ? <Skeleton className="h-64" /> : (
        <div className="space-y-3">
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40">
            <ul className="divide-y divide-zinc-800/40">
              {groups.map((g) => (
                <li key={g.key}>
                  {/* Several people on one ticket read grouped under their
                      own names, each with their own total, the way her old
                      system showed a family: "each patient gets a seperated
                      receipt with only there services on the receipt". */}
                  {groups.length > 1 && (
                    <div className="flex items-center justify-between gap-3 bg-zinc-900/40 px-3 py-1.5">
                      <span className="truncate text-xs font-semibold text-sky-300">
                        {g.name || "On this bill"}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-zinc-400">
                        {formatMoney(g.total)}
                      </span>
                    </div>
                  )}
                  <ul className="divide-y divide-zinc-800/40">
                    {g.lines.map((l, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                        <span className="min-w-0 text-zinc-200">
                          {l.name}{l.qty > 1 ? <span className="text-zinc-500"> ×{l.qty}</span> : null}
                          {l.meta?.redeem_package_balance_id ? (
                            <Badge color="violet" size="xs" className="ml-2">Voucher</Badge>
                          ) : null}
                        </span>
                        <span className="shrink-0 tabular-nums text-zinc-300">
                          {formatMoney(l.unit_amount_cents * l.qty)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
              <li className="flex items-center justify-between bg-zinc-900/60 px-3 py-2 font-semibold">
                <span className="text-zinc-100">Total</span>
                <span className="tabular-nums text-zinc-100">{formatMoney(order.total_cents)}</span>
              </li>
            </ul>
          </div>

          {order.status === "void" && (
            <div className="rounded-lg border border-zinc-700 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-300">
              This sale was voided - it is off your takings, and nothing on it was paid.
            </div>
          )}

          {online && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm">
              <p className="font-medium text-emerald-200">
                Online order, {meta.delivery === "ship" ? "to ship" : "for pickup at the clinic"}
              </p>
              {meta.delivery === "ship" && meta.ship_to && (
                <p className="mt-0.5 text-zinc-300">
                  {[meta.ship_to.name, meta.ship_to.line1, meta.ship_to.line2,
                    [meta.ship_to.city, meta.ship_to.state, meta.ship_to.postal_code].filter(Boolean).join(" ")]
                    .filter(Boolean).join(", ")}
                </p>
              )}
              {meta.notes && <p className="mt-0.5 text-zinc-400">Note: {meta.notes}</p>}
              {order.status === "paid" && (
                fulfilledAt
                  ? <p className="mt-1 text-xs text-zinc-500">{meta.delivery === "ship" ? "Shipped" : "Picked up"} {formatDate(fulfilledAt, { month: "short", day: "numeric" })}</p>
                  : <Button size="sm" variant="secondary" className="mt-2" onClick={() => void markFulfilled()}>
                      {meta.delivery === "ship" ? "Mark as shipped" : "Mark as picked up"}
                    </Button>
              )}
            </div>
          )}

          {/* How it was paid — the thing that settles voucher arguments. */}
          <div>
            <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">How it was paid</h4>
            <ul className="space-y-1 text-sm">
              {data.payments.map((p, i) => (
                <li key={i} className="flex justify-between text-zinc-300">
                  <span>{{ gift_card: "Gift card", account_credit: "Account credit", cash: "Cash" }[p.provider] ?? "Card"}</span>
                  <span className="tabular-nums">{formatMoney(Number(p.amount_cents))}</span>
                </li>
              ))}
              {data.redemptions.map((r, i) => (
                <li key={`v${i}`} className="flex justify-between text-violet-300">
                  <span>Voucher — {r.service_name ?? r.package_name}{r.sessions > 1 ? ` ×${r.sessions}` : ""}</span>
                  <span className="tabular-nums">{formatMoney(Number(r.price_cents) * r.sessions)}</span>
                </li>
              ))}
              {data.payments.length === 0 && data.redemptions.length === 0 && (
                <li className="text-zinc-500">No payment recorded.</li>
              )}
            </ul>
          </div>

          {data.refunds.length > 0 && (
            <div>
              <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-rose-300">Refunded</h4>
              <ul className="space-y-1 text-sm">
                {data.refunds.map((r) => (
                  <li key={r.id} className="flex justify-between text-zinc-400">
                    <span>
                      {formatDate(r.created_at, { month: "short", day: "numeric" })}
                      {r.reason ? ` — ${r.reason}` : ""}
                      {r.created_by_name ? <span className="text-zinc-600"> · {r.created_by_name}</span> : null}
                    </span>
                    <span className="tabular-nums text-rose-300">({formatMoney(Number(r.amount_cents))})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <ModalFooter>
        <Button variant="secondary" onClick={() => window.print()}>
          <Printer className="h-3.5 w-3.5" /> Print
        </Button>
        {canVoid && (
          <Button variant="secondary" onClick={() => void doVoid()} loading={voiding}
            className="text-amber-300 hover:text-amber-200">
            <Ban className="h-3.5 w-3.5" /> It never got paid
          </Button>
        )}
        {left > 0 && !voided && (
          <Button variant="secondary" onClick={() => setRefunding(true)}
            className="text-rose-300 hover:text-rose-200">
            <RotateCcw className="h-3.5 w-3.5" /> Refund
          </Button>
        )}
        <Button onClick={onClose}>Done</Button>
      </ModalFooter>

      {refunding && (
        <RefundModal orderId={order.id} max={left}
          products={lines.filter((l) => l.kind === "product" && typeof l.meta?.product_id === "string")
            .map((l) => ({ product_id: l.meta!.product_id as string, name: l.name, qty: l.qty }))}
          onClose={() => setRefunding(false)}
          onDone={() => { setRefunding(false); load(); onChanged(); toast.success("Refund recorded"); }} />
      )}
    </Modal>
  );
}

function RefundModal({
  orderId, max, products, onClose, onDone,
}: {
  orderId: string; max: number;
  /** Retail lines on the receipt, so returned items can go back in stock. */
  products: { product_id: string; name: string; qty: number }[];
  onClose: () => void; onDone: () => void;
}) {
  const toast = useToast();
  const [amount, setAmount] = useState((max / 100).toFixed(2));
  const [method, setMethod] = useState("cash");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [restock, setRestock] = useState<Record<string, number>>({});

  const go = async () => {
    setSaving(true);
    try {
      await api.post(`/pos/orders/${orderId}/refund`, {
        amount_cents: Math.round(parseFloat(amount || "0") * 100), method, reason,
        restock: Object.entries(restock).filter(([, q]) => q > 0).map(([product_id, qty]) => ({ product_id, qty })),
      });
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't record that refund");
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="Refund">
      <div className="space-y-3">
        <p className="text-sm text-zinc-400">
          Up to <span className="font-semibold text-zinc-200">{formatMoney(max)}</span> can still be refunded on this receipt.
        </p>
        <div>
          <label className="mb-1 block text-xs text-zinc-400">How much?</label>
          <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" autoFocus />
        </div>
        <div>
          <label className="mb-1 block text-xs text-zinc-400">Give it back as</label>
          <Select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="cash">Cash from the drawer</option>
            <option value="card">Back to their card</option>
            <option value="account_credit">Account credit</option>
            <option value="gift_card">Gift card</option>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-zinc-400">Why?</label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Reaction to the drip" />
        </div>
        {products.length > 0 && (
          <div>
            <label className="mb-1 block text-xs text-zinc-400">Products handed back that can be sold again</label>
            <div className="space-y-1.5">
              {products.map((p) => (
                <div key={p.product_id} className="flex items-center justify-between gap-2 text-sm text-zinc-300">
                  <span className="min-w-0 truncate">{p.name}</span>
                  <span className="flex shrink-0 items-center gap-1.5 text-xs text-zinc-500">
                    back in stock
                    <Input className="h-7 w-16" inputMode="numeric" value={String(restock[p.product_id] ?? 0)}
                      onChange={(e) => setRestock((r) => ({ ...r, [p.product_id]: Math.max(0, Math.min(p.qty, parseInt(e.target.value, 10) || 0)) }))} />
                    of {p.qty}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={go} disabled={saving || !amount}>Refund {formatMoney(Math.round(parseFloat(amount || "0") * 100))}</Button>
      </ModalFooter>
    </Modal>
  );
}
